import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import CSIProgrammesData from '../ExecutiveDash/CSIProgrammesData';
import UserAvatarSVG from '@/components/UserAvatarSVG';
import '@/styles/CSIDashStyles.scss';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import * as Sentry from '@sentry/react';

const CsiDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [period, setPeriod] = useState(null);
    const [csiData, setCsiData] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch(`/api/Mda/DefaultPeriod`);
                const data = await response.json();
                setPeriod(data.period1);
            } catch (e) {
                Sentry.captureException(e);
                if (e.message !== 'Token refresh failed') {
                    console.error(e);
                }
            }
        };
        fetchDefaultPeriod();
    }, [apiFetch]);

    useEffect(() => {
        const timer = setTimeout(() => setInitialLoading(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    const fetchCSIData = async () => {
        try {
            const response = await apiFetch('/api/marketing/get-all-csi-project-data');
            const data = await response.json();
            if (data.success && data.data) {
                setCsiData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCSIData();
        }, 0);
        return () => clearTimeout(timer);
    }, [fetchCSIData]);

    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return { monthName: monthNames[parseInt(month, 10)], year, prevYear };
    }

    const { monthName, year } = getMonthYear(period);

    function getYearStartPeriod(period) {
        if (!period) return null;
        const periodStr = period.toString().padStart(6, '0');
        return parseInt(periodStr.substring(0, 4) + '01', 10);
    }

    function getYearToDateSums(data, csiCategoryID, period) {
        if (!period) return {};
        const yearStart = getYearStartPeriod(period);
        if (!yearStart) return {};
        return data
            .filter(d => d.csiCategoryID === csiCategoryID && d.period >= yearStart && d.period <= period)
            .reduce((acc, d) => {
                acc[d.projectName] = (acc[d.projectName] || 0) + (d.count || 0);
                return acc;
            }, {});
    }

    function getTotalSums(data, csiCategoryID, period) {
        if (!period) return {};
        return data
            .filter(d => d.csiCategoryID === csiCategoryID && d.period <= period)
            .reduce((acc, d) => {
                acc[d.projectName] = (acc[d.projectName] || 0) + (d.count || 0);
                return acc;
            }, {});
    }

    function sumFieldForProject(data, projectName, field, csiCategoryID, periodStart, periodEnd) {
        return data
            .filter(d => d.projectName === projectName && d.csiCategoryID === csiCategoryID && d.period >= periodStart && d.period <= periodEnd)
            .reduce((acc, d) => acc + (d[field] || 0), 0);
    }

    function sumFieldForProjectTotal(data, projectName, field, csiCategoryID, periodEnd) {
        return data
            .filter(d => d.projectName === projectName && d.csiCategoryID === csiCategoryID && d.period <= periodEnd)
            .reduce((acc, d) => acc + (d[field] || 0), 0);
    }

    const yearToDateSums = getYearToDateSums(csiData, 1, period);
    const totalSums = getTotalSums(csiData, 1, period);

    const barefootYTD = yearToDateSums["Barefoot No More Project"] || 0;
    const barefootTotal = totalSums["Barefoot No More Project"] || 0;
    const lapDeskYTD = yearToDateSums["Lap Desk Project"] || 0;
    const lapDeskTotal = totalSums["Lap Desk Project"] || 0;
    const rialtoYTD = yearToDateSums["The Rialto Project"] || 0;
    const rialtoTotal = totalSums["The Rialto Project"] || 0;
    const cangroYTD = yearToDateSums["The Cangro Project"] || 0;
    const cangroTotal = totalSums["The Cangro Project"] || 0;
    const dignityYTD = yearToDateSums["Project Dignity"] || 0;
    const dignityTotal = totalSums["Project Dignity"] || 0;

    const yearStart = getYearStartPeriod(period);

    const sportingCommunitiesYTD = sumFieldForProject(csiData, "Sporting Chance Programmes", "communityCount", 2, yearStart, period);
    const sportingProgrammesYTD = sumFieldForProject(csiData, "Sporting Chance Programmes", "programCount", 2, yearStart, period);
    const sportingParticipantsYTD = sumFieldForProject(csiData, "Sporting Chance Programmes", "count", 2, yearStart, period);
    const sportingCommunitiesTotal = sumFieldForProjectTotal(csiData, "Sporting Chance Programmes", "communityCount", 2, period);
    const sportingProgrammesTotal = sumFieldForProjectTotal(csiData, "Sporting Chance Programmes", "programCount", 2, period);
    const sportingParticipantsTotal = sumFieldForProjectTotal(csiData, "Sporting Chance Programmes", "count", 2, period);

    const soccerNetballYTD = sumFieldForProject(csiData, "Soccer/Netball Team Project", "count", 2, yearStart, period);
    const soccerNetballTotal = sumFieldForProjectTotal(csiData, "Soccer/Netball Team Project", "count", 2, period);

    const memezaYTD = sumFieldForProject(csiData, "Memeza Project", "count", 3, yearStart, period);
    const memezaTotal = sumFieldForProjectTotal(csiData, "Memeza Project", "count", 3, period);

    const foodSecGardensYTD = sumFieldForProject(csiData, "Sustainable Food Security Project", "communityCount", 3, yearStart, period);
    const foodSecBucketsYTD = sumFieldForProject(csiData, "Sustainable Food Security Project", "count", 3, yearStart, period);
    const foodSecGardensTotal = sumFieldForProjectTotal(csiData, "Sustainable Food Security Project", "communityCount", 3, period);
    const foodSecBucketsTotal = sumFieldForProjectTotal(csiData, "Sustainable Food Security Project", "count", 3, period);

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div>
                    <div>
                        <h1 className="dashboard-greeting">
                            <UserAvatarSVG />
                            Hello {user.FirstName || user.firstName}
                        </h1>
                        <h3>CSI Project Dashboard</h3>
                    </div>
                    <div className="marketing-dashboard__grid">
                        {/* Top row */}
                        <div className="marketing-dashboard__row">
                            <div className="marketing-dashboard__cell full-height">
                                <CSIProgrammesData period={period} />
                            </div>
                            <div className="marketing-dashboard__cell full-height">
                                <table className="csi-milestones-table">
                                    <thead>
                                        <tr>
                                            <th className="csi-milestones-table__main-header">CSI Project Milestone</th>
                                            <th className="csi-milestones-table__period-header">
                                                Year to Date<br /><span>{monthName} {year}</span>
                                            </th>
                                            <th className="csi-milestones-table__period-header">
                                                Total<br /><span>Since 2014</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="csi-milestones-table__category" colSpan={3}>Sport for Youth</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Sporting Chance Programmes</strong>
                                                <div className="csi-milestones-table__desc">Number of communities</div>
                                                <div className="csi-milestones-table__desc">Number of programmes</div>
                                                <div className="csi-milestones-table__desc">Number of participants</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>{sportingCommunitiesYTD}</div>
                                                <div>{sportingProgrammesYTD}</div>
                                                <div>{sportingParticipantsYTD}</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>{sportingCommunitiesTotal}</div>
                                                <div>{sportingProgrammesTotal}</div>
                                                <div>{sportingParticipantsTotal}</div>
                                            </td>
                                        </tr>
                                        <tr className="csi-milestones-table__alt">
                                            <td>
                                                <strong>Soccer/Netball Team Project</strong>
                                                <div className="csi-milestones-table__desc">Teams sponsored</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {soccerNetballYTD}
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {soccerNetballTotal}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* Second row */}
                        <div className="marketing-dashboard__row">
                            <div className="marketing-dashboard__cell full-height">
                                <table className="csi-milestones-table">
                                    <thead>
                                        <tr>
                                            <th className="csi-milestones-table__main-header">CSI Project Milestones</th>
                                            <th className="csi-milestones-table__period-header">
                                                Year to Date<br /><span>{monthName} {year}</span>
                                            </th>
                                            <th className="csi-milestones-table__period-header">
                                                Total<br /><span>Since 2014</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="csi-milestones-table__category" colSpan={3}>Educational Support</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Barefoot No More Project</strong>
                                                <div className="csi-milestones-table__desc">School shoes donated</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {barefootYTD}
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {barefootTotal}
                                            </td>
                                        </tr>
                                        <tr className="csi-milestones-table__alt">
                                            <td>
                                                <strong>Academic Prize Programme</strong>
                                                <div className="csi-milestones-table__desc">Academic prizes awarded</div>
                                                <div className="csi-milestones-table__desc">Number of participating schools</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>{lapDeskYTD}</div>
                                                <div>0</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>{lapDeskTotal}</div>
                                                <div>0</div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>The Rialto Project</strong>
                                                <div className="csi-milestones-table__desc">Bursaries sponsored</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {rialtoYTD}
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {rialtoTotal}
                                            </td>
                                        </tr>
                                        <tr className="csi-milestones-table__alt">
                                            <td>
                                                <strong>The Cangro Project</strong>
                                                <div className="csi-milestones-table__desc">Cangro cans donated</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {cangroYTD}
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {cangroTotal}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Project Dignity</strong>
                                                <div className="csi-milestones-table__desc">Sustainable sanitary products donated</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {dignityYTD}
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {dignityTotal}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="marketing-dashboard__cell">
                                <table className="csi-milestones-table">
                                    <thead>
                                        <tr>
                                            <th className="csi-milestones-table__main-header">CSI Project Milestones</th>
                                            <th className="csi-milestones-table__period-header">
                                                Year to Date<br /><span>{monthName} {year}</span>
                                            </th>
                                            <th className="csi-milestones-table__period-header">
                                                Total<br /><span>Since 2014</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="csi-milestones-table__category" colSpan={3}>Community Upliftment</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Memeza Project</strong>
                                                <div className="csi-milestones-table__desc">Number of personal alarms donated</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {memezaYTD}
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                {memezaTotal}
                                            </td>
                                        </tr>
                                        <tr className="csi-milestones-table__alt">
                                            <td>
                                                <strong>Sustainable Food Security Project</strong>
                                                <div className="csi-milestones-table__desc">Community/school gardens supported</div>
                                                <div className="csi-milestones-table__desc">Number of garden in a bucket kits donated</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>{foodSecGardensYTD}</div>
                                                <div>{foodSecBucketsYTD}</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>{foodSecGardensTotal}</div>
                                                <div>{foodSecBucketsTotal}</div>
                                            </td>
                                        </tr>
                                        <tr className="csi-milestones-table__alt">
                                            <td>
                                                <strong>Community Feeding Project</strong>
                                                <div className="csi-milestones-table__desc">Communities supported</div>
                                                <div className="csi-milestones-table__desc">Meals provided</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>0</div>
                                                <div>0</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>0</div>
                                                <div>0</div>
                                            </td>
                                        </tr>
                                        <tr className="csi-milestones-table__alt">
                                            <td>
                                                <strong>SASSA Day Elderly Support Project</strong>
                                                <div className="csi-milestones-table__desc">Communities supported</div>
                                                <div className="csi-milestones-table__desc">Meals provided</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>0</div>
                                                <div>0</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>0</div>
                                                <div>0</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* Third row */}
                        <div className="marketing-dashboard__row">
                            <div className="marketing-dashboard__cell half-width">
                                <table className="csi-milestones-table">
                                    <thead>
                                        <tr>
                                            <th className="csi-milestones-table__main-header">CSI Project Milestones</th>
                                            <th className="csi-milestones-table__period-header">
                                                Year to Date<br /><span>{monthName} {year}</span>
                                            </th>
                                            <th className="csi-milestones-table__period-header">
                                                Total<br /><span>Since 2014</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="csi-milestones-table__category" colSpan={3}>Community Healthcare</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Unjani Clinic</strong>
                                                <div className="csi-milestones-table__desc">Number of Unjani Clinics established</div>
                                                <div className="csi-milestones-table__desc">Number of patient consultations</div>
                                                <div className="csi-milestones-table__desc">Number of Pink Drives</div>
                                                <div className="csi-milestones-table__desc">Number of patients referred</div>
                                            </td>
                                            <td className="csi-milestones-table__value bold">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>0</div>
                                                <div>0</div>
                                                <div>0</div>
                                                <div>0</div>
                                            </td>
                                            <td className="csi-milestones-table__value">
                                                <div className="csi-milestones-table__spacer"></div>
                                                <div>0</div>
                                                <div>0</div>
                                                <div>0</div>
                                                <div>0</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CsiDashboard;